// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Pressable, StyleSheet, type ViewStyle } from 'react-native';

import { Text, View } from '@/components/Themed';
import appColors from '@/theme/colors';

type AuthStatusCardProps = {
  title: string;
  status?: string | null;
  error?: string | null;
  buttonLabel?: string;
  onPress?: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
  style?: ViewStyle;
  buttonTestID?: string;
};

export const AuthStatusCard = ({ title, status, error, buttonLabel, onPress, disabled, children, style, buttonTestID }: AuthStatusCardProps) => {
  return (
    <View style={[styles.card, style]}>
      <Text style={styles.title}>{title}</Text>
      {children}
      {status ? <Text style={styles.status}>{status}</Text> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}
      {buttonLabel && onPress ? (
        <Pressable
          accessibilityRole="button"
          testID={buttonTestID ?? 'auth-status-card-action'}
          style={[styles.button, disabled ? styles.buttonDisabled : null]}
          onPress={onPress}
          disabled={disabled}>
          <Text style={styles.buttonText}>{buttonLabel}</Text>
        </Pressable>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: appColors.background.panel,
    borderRadius: 16,
    padding: 18,
    gap: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  status: {
    color: appColors.text.accentSoft,
  },
  error: {
    color: appColors.status.errorText,
  },
  button: {
    marginTop: 8,
    backgroundColor: appColors.background.interactive,
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: appColors.border.accent,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: appColors.text.primary,
    fontWeight: '800',
    fontSize: 16,
  },
});
